<template>
  <div class="analytics_filter_component">
    <ViewFilterComponent
      source="sequence_analytics_filter_component"
      :entire-data="true"
      :filters-options="filteredOptions"
      :filter-data="filterData"
      @filter-changed="handleFilterChange"
      @clear-all-filters="clearAllFilters"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { analyticsFilterEntity, multiSelectFilters } from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import ViewFilterComponent from "@/src/components/view_components/ViewFilterComponent.vue";
import dayjs from "dayjs";

const props = defineProps({
  allowedFilters: { type: Array, required: true },
});

const filterData = ref({});
const filterDataEntire = ref({});

const analyticsFilter = [
  {
    id: analyticsFilterEntity.USER,
    name: "User",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "date",
    name: "Date",
    options: [
      { id: "today", name: "Today" },
      { id: "yesterday", name: "Yesterday" },
      { id: "this_week", name: "This Week" },
      { id: "last_30_days", name: "Last 30 days" },
      { id: "this_month", name: "This Month" },
      { id: "last_month", name: "Last Month" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

const filteredOptions = computed(() => {
  return analyticsFilter.filter((filter) =>
    props.allowedFilters.includes(filter.id),
  );
});

const clearAllFilters = async () => {
  filterData.value = {
    user: [],
    date: "last_30_days",
  };

  filterDataEntire.value = {
    user: [],
    date: { option: "last_30_days" },
  };
  
  // Set default to last_30_days and select the filter
  const defaultRange = calculateDateRange("last_30_days");
  await store.dispatch("analytics/updateRange", defaultRange);
  await handleFilterApply();
};

const calculateDateRange = (option) => {
  const now = dayjs();
  let start, end;

  switch (option) {
    case "today":
      start = now.startOf("day");
      end = now.endOf("day");
      break;
    case "yesterday":
      start = now.subtract(1, "day").startOf("day");
      end = now.subtract(1, "day").endOf("day");
      break;
    case "this_week":
      start = now.startOf("week");
      end = now.endOf("week");
      break;
    case "last_30_days":
      start = now.subtract(29, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "this_month":
      start = now.startOf("month");
      end = now.endOf("month");
      break;
    case "last_month":
      start = now.subtract(1, "month").startOf("month");
      end = now.subtract(1, "month").endOf("month");
      break;
    case "custom":
      // For custom, we'll use the provided dates or default to last 30 days
      start = now.subtract(29, "day").startOf("day");
      end = now.endOf("day");
      break;
    default:
      start = now.subtract(1, "year").startOf("day");
      end = now.endOf("day");
  }

  return {
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    key: option,
    label: analyticsFilter.find(f => f.id === "date")?.options.find(opt => opt.id === option)?.name || option
  };
};

const handleDateRangeUpdate = (dateObject) => {
  // Convert the date object from ViewFilterComponent to the format expected by updateRange
  if (dateObject && (dateObject.option || dateObject.id)) {
    const option = dateObject.option || dateObject.id;
    let rangeObject;
    
    if (option === "custom" && dateObject.start_date && dateObject.end_date) {
      // Use provided custom dates
      rangeObject = {
        key: "custom",
        start: dateObject.start_date,
        end: dateObject.end_date,
        label: `${dateObject.start_date} to ${dateObject.end_date}`
      };
    } else {
      // Calculate date range for predefined options
      rangeObject = calculateDateRange(option);
    }
    
    store.dispatch("analytics/updateRange", rangeObject);
  } else {
    // Set default to last_30_days when no date is provided
    const defaultRange = calculateDateRange("last_30_days");
    store.dispatch("analytics/updateRange", defaultRange);
  }
};

const handleFilterChange = ({ filter, updated_values }) => {
  try {
    if (updated_values) {
      if (filter === "date") {
        // Handle date filter - store in filterData but also update range
        filterData.value[filter] = updated_values?.option || updated_values?.id;
        filterDataEntire.value[filter] = updated_values;
        handleDateRangeUpdate(updated_values);
      } else if (multiSelectFilters.includes(filter)) {
        if (!filterDataEntire.value[filter]) {
          filterDataEntire.value[filter] = [];
        }
        if (!filterData.value[filter]) {
          filterData.value[filter] = [];
        }

        const existingIndex = filterDataEntire.value[filter].findIndex(
          (item) => item.id === updated_values.id,
        );

        if (existingIndex === -1) {
          filterDataEntire.value[filter] = [
            ...filterDataEntire.value[filter],
            updated_values,
          ];
          filterData.value[filter] = [
            ...new Set([...filterData.value[filter], updated_values.id]),
          ];
        } else {
          const newArray = [...filterDataEntire.value[filter]];
          newArray.splice(existingIndex, 1);
          filterDataEntire.value[filter] = newArray;
          filterData.value[filter] = newArray.map((item) => item.id);
        }
      } else {
        filterData.value[filter] = updated_values?.id;
        filterDataEntire.value[filter] = updated_values;
      }
    } else {
      if (filter === "date") {
        // Clear date filter
        delete filterData.value[filter];
        delete filterDataEntire.value[filter];
        handleDateRangeUpdate(null);
      } else if (filter in filterData.value) {
        if (multiSelectFilters.includes(filter)) {
          filterDataEntire.value[filter] = [];
          filterData.value[filter] = [];
        } else {
          delete filterData.value[filter];
          delete filterDataEntire.value[filter];
        }
      }
    }

    // Only call handleFilterApply for non-date filters since date is handled separately
    if (filter !== "date") {
      handleFilterApply();
    }
  } catch (error) {
    console.error("An error occurred while handling filter change: ", error);
  }
};

const handleFilterApply = async () => {
  const filtersToStore = { ...filterDataEntire.value };

  multiSelectFilters.forEach((filter) => {
    if (filtersToStore[filter] && Array.isArray(filtersToStore[filter])) {
      const uniqueItems = [];
      const seenIds = new Set();

      filtersToStore[filter].forEach((item) => {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          uniqueItems.push(item);
        }
      });

      filtersToStore[filter] = uniqueItems;
      filterData.value[filter] = Array.from(seenIds);
    }
  });

  // Preserve existing filters (especially sequence ID) that are not managed by this component
  const currentFilters = store.getters["analytics/getFilters"] || {};
  const mergedFilters = {
    ...currentFilters,
    ...filtersToStore
  };

  await store.dispatch("analytics/updateFilters", mergedFilters);
};

const setFilterOptions = (filter) => {
  switch (filter.id) {
    case analyticsFilterEntity.USER:
      filter.options = store.getters["analytics/getUsersList"] || [];
      break;
  }
};

const initializeFilterOptions = () => {
  analyticsFilter.forEach((filter) => {
    if (props.allowedFilters.includes(filter.id)) {
      setFilterOptions(filter);
    }
  });
};

// Watch for users list changes and update filter options
watch(
  () => store.getters["analytics/getUsersList"],
  (newUsersList) => {
    if (newUsersList && newUsersList.length > 0) {
      initializeFilterOptions();
    }
  },
  { deep: true }
);

onMounted(() => {
  const data = store.getters["analytics/getFilters"] || {};
  const range = store.getters["analytics/getRange"];

  // Set default date filter to "last_30_days" if no date filter is set
  const defaultDateOption = "last_30_days";
  const currentDateFilter = range || null;
  
  filterDataEntire.value = {
    user: Array.isArray(data.user)
      ? [...data.user]
      : data.user
        ? [data.user]
        : [],
    date: currentDateFilter || { option: defaultDateOption },
  };

  filterData.value = {
    user: filterDataEntire.value.user.map((item) => item.id),
    date: filterDataEntire.value.date?.key || filterDataEntire.value.date?.option || defaultDateOption,
  };

  // Initialize filter options
  initializeFilterOptions();

  // If no date range is set, set the default and select the filter
  if (!currentDateFilter) {
    const defaultRange = calculateDateRange(defaultDateOption);
    store.dispatch("analytics/updateRange", defaultRange);
    filterDataEntire.value.date = { option: defaultDateOption };
    filterData.value.date = defaultDateOption;
  }

  handleFilterApply();
});
</script>
